import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames/bind';
import TreeSelectionHandler from '../../lib/TreeSelectionHandler';
import createFakeEvent from '../../lib/createFakeEvent';
import DefaultTreeRenderer from './DefaultTreeRenderer';
import InfiniteScroll from '../InfiniteScroll/InfiniteScroll';
import Progressbar from '../Progressbar/Progressbar';
import Node from './Node';
import LoadingIndicator from './LoadingIndicator';

/**
 * `Tree` is a tree component that can display hierarchical data with support for collapsible nodes, multiple columns and
 * pagination.
 *
 * The component will automatically generate the tree for you based on the value of *nestedKey*. So if you have tree
 * structure where each node has its children in a property named "elements", just set *nestedKey* to "elements"" as and the
 * component will automatically show these items when expanding a tree node.
 *
 * The tree will show a "Loading..." indicator when expanding folders that have an empty children array. Use the
 * *onExpandCollapse* callback to listen for expand/collapse events.
 *
 * If the component is passed `null` or `undefined` as *items* value, the component will display a "No items found". If
 * *items* is an empty array, "Loading..." will be displayed instead.
 *
 * The component has a function `scrollToCell` which scrolls the tree to make a cell visible. The parent folders must be
 * expanded. The function takes a string argument representing the key of the cell, e.g. "0.0.12" representing the first
 * child of the root level, the first child on the next level and the 12th child in the final level.
 *
 * The `Tree` component is built on top of the HTML `table` element to get support for columns.
 *
 * @property {node} children The elements you want to add to the `Tree` component. Since `Tree` is based on {@link Table}
 * you should use `thead`, `tbody`, `tr`, `th` and `td` elements. Alternatively you can just pass your tree structure to the
 * *items* prop and have the items created for you automatically.
 * @property {string} className The class name for this component. Will be appended to "core-tree core-table".
 * @property {array} columns Array of attribute names that will be shown for each tree node. Defaults to ["title"] meaning
 * that only the *title* attribute will be shown by default.
 * @property {array} columnNames Array of column names in case you don't want to expose the attribute names directly. Must
 * be of the same length as *columns*.
 * @property {string} emptyText The text to display when *items* is `null` or `undefined`. Pass an empty array to display
 * the text set by *loadingText* instead. Defaults to "No items found".
 * @property {object} expanded An object that holds the expand state of every node. The object keys must be the item paths,
 * e.g. "0" will indicate the first root element. "0.0" the first item in the first root element and so on.
 * @property {function} hasMore A callback that should return `true` when there are more content to be loaded. *loadMore*
 * will only be called as long as *hasMore* returns `true`.
 * @property {string} iconName The name of the icon you want to use for indicating expanded and collapsed state. Defaults to
 * "arrow-right".
 * @property {function} innerRef Returns reference to this tree component
 * @property {function} itemRenderer The cell renderer for all items in *items*. The built in renderer will render a folder
 * icon in the first column and a stringified version of the value for any given column.
 * @property {array} items An array of root objects that will rendered at the root level. Children will be retrieved from
 * each node's *nestedKey* property. You can include `null` elements in *items* to add row separators.
 * @property {string} nestedKey This prop determines which attribute to use when getting children for a given node. Defaults
 * to "children".
 * @property {boolean} loading If set to `true`, the tree will display a spinner indicator. Defaults to `false`.
 * @property {string} loadingText The text to display when a folder does not contain any children. Defaults to "Loading...".
 * Setting this prop to null or undefined will prevent the text from being shown.
 * @property {function} loadMore A callback that triggers loading of more content. *loadMore* will only be called as long as
 * your *loadMore* callback returns `true`.
 * @property {function} onClick A Callback that is triggered whenever a tree node is clicked. Will not be called when the
 * user expands or collapses a tree node - use *onExpandCollapse* for that. The first parameter to this function is the
 * source event where `e.target.value` is the node object itself. Two more parameters are passed to the callback: index of
 * column that the user clicked on and a generated string key indicating the indexes in the children array for the entire
 * parent path. E.g. a path of "0.1.0.1" will indicate the first node at root level, the second node under that node and so
 * on. The last element is the column the the user clicked on.
 * @property {function} onCloseMenu A callback that is called when an item's popup menu is about to close. Will only be
 * called when *onCreateMenuItems* is also set.
 * @property {function} onCreateMenuItems If this callback is set the tree component will add an extra column with a menu
 * button in it. Clicking on this button will call this function and display whatever components you return. The row item
 * will be passed to the function. You may keep this as a reference for your actions. You may use *onCloseMenu* to clear this
 * reference (e.g. to allow your app to work on the selection and not the item that triggered the menu).
 * @property {function} onExpandCollapse A callback that is triggered whenever a tree node is expanded or collapsed. The
 * callback will pass another 3 parameters, cell identifier, expand state and the tree's entire expand state. Expanding and
 * collapsing nodes will not trigger *onClick*.
 * @property {function} pageStart A callback which returns the next page index. `Tree` will only call *hasMore* and
 * *loadMore* when *pageStart* returns a different page index that is not equal to the last one returned.
 * @property {boolean} showHeader If set to `true` the tree component will display the labels for all columns. Defaults
 * to `false`.
 * @property {number} threshold The number of pixels from the bottom of the container that has to be visible before the
 * pagination callbacks are called. Defaults to 400px.
 * @property {boolean} lazyLoadChildren Add a button to lazy load children of nodes with hasMore attribute set to true
 * @property {boolean} lazyLoadChildrenText Text to be displayed on button for lazy loading nested children
 *
 * @see Table
 */
export default class Tree extends Component {

  static displayName = 'Tree';

  static propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    columnNames: PropTypes.arrayOf(PropTypes.string),
    columns: PropTypes.arrayOf(PropTypes.string),
    emptyText: PropTypes.string,
    expanded: PropTypes.object,
    hasMore: PropTypes.func,
    iconName: PropTypes.string,
    id: PropTypes.string,
    innerRef: PropTypes.func,
    itemDisabledKey: PropTypes.string,
    itemRenderer: PropTypes.func.isRequired,
    items: PropTypes.array,
    lazyLoadChildren: PropTypes.bool,
    lazyLoadChildrenText: PropTypes.string,
    loading: PropTypes.bool,
    loadingText: PropTypes.string,
    loadMore: PropTypes.func,
    nestedKey: PropTypes.string,
    onClick: PropTypes.func,
    onCloseMenu: PropTypes.func,
    onCreateMenuItems: PropTypes.func,
    onExpandCollapse: PropTypes.func,
    onSelectionChanged: PropTypes.func,
    pageStart: PropTypes.func,
    selection: PropTypes.object,
    selectionType: PropTypes.string,
    showHeader: PropTypes.bool,
    style: PropTypes.object,
  };

  static defaultProps = {
    columns: ['title'],
    emptyText: 'No items found',
    getSelectionHandler: () => { },
    iconName: 'arrow-right',
    innerRef: () => { },
    itemRenderer: (item, column, columnIndex) =>
      <DefaultTreeRenderer item={item} column={column} columnIndex={columnIndex} />,
    lazyLoadChildren: false,
    loadingText: 'Loading...',
    nestedKey: 'children',
    selection: {},
    selectionType: TreeSelectionHandler.SINGLE,
    showHeader: false,
    style: {},
  };

  static getNodeFromKey(items = [], nestedKey, key = '') {
    const indexes = key.split('.').map(id => parseInt(id, 10));
    let item;

    for (let i = 0; i <= indexes.length - 1; i++) {
      item = items[indexes[i]];
      items = item[nestedKey]; // eslint-disable-line no-param-reassign
    }
    return item;
  }

  constructor(props) {
    super(props);
    this.selectionHandler = new TreeSelectionHandler(props.selectionType);
    props.getSelectionHandler(this.selectionHandler);
    this.refTree = null;
    this.state = { expanded: props.expanded || {} };
  }

  componentDidUpdate(prevProps) {
    if (this.props.expanded !== prevProps.expanded) {
      this.setState({ expanded: this.props.expanded });
    }
    if (
      this.props.selectionType !== prevProps.selectionType ||
      this.props.nestedKey !== prevProps.nestedKey
    ) {
      this.selectionHandler = new TreeSelectionHandler(this.props.selectionType, this.props.nestedKey);
      this.props.getSelectionHandler(this.selectionHandler);
    }
  }

  onClick = e => {
    const { itemDisabledKey, items, nestedKey, onClick, onSelectionChanged, selection } = this.props;

    const cell = e.currentTarget.dataset.key;
    const row = cell.substr(0, e.currentTarget.dataset.key.lastIndexOf('.'));
    const indexes = cell.split('.');
    const column = parseInt(indexes[indexes.length - 1], 10);

    const item = Tree.getNodeFromKey(items, nestedKey, row);
    if (itemDisabledKey && !item[itemDisabledKey]) {
      return;
    }

    if (onClick) {
      onClick(createFakeEvent(item, e), column, cell);
    }
    if (onSelectionChanged) {
      const newSelection = this.selectionHandler.handleSelectionEvent(items, row, selection, e);

      if (newSelection !== selection) {
        onSelectionChanged(createFakeEvent(newSelection, e, this.refTree.dataset));
      }
    }
  };

  onExpandCollapse = e => {
    const key = e.target.parentNode.dataset.key.substr(0, e.target.parentNode.dataset.key.lastIndexOf('.'));
    const expanded = { ...this.state.expanded };

    e.stopPropagation();
    expanded[key] = !expanded[key];
    this.setState({ expanded });
    const item = Tree.getNodeFromKey(this.props.items, this.props.nestedKey, key);
    if (this.props.onExpandCollapse) {
      this.props.onExpandCollapse(createFakeEvent(item, e, this.refTree.dataset), key, expanded[key], expanded);
    }
  };

  onLoadChildren = e => {
    e.stopPropagation();
    const key = e.currentTarget.dataset.key;
    const item = Tree.getNodeFromKey(this.props.items, this.props.nestedKey, key);
    if (this.props.onLoadChildren) {
      this.props.onLoadChildren(createFakeEvent(item, e), key);
    }
  };

  scrollToCell = cell => {
    if (this.refTree) {
      const td = this.refTree.querySelector(`td[data-key="${cell}.0"]`);

      if (td && td.scrollIntoViewIfNeeded) {
        td.scrollIntoViewIfNeeded();
      }
    }
  };

  render() {
    const {
      children,
      className,
      columnNames,
      columns,
      emptyText,
      hasMore,
      getSelectionHandler,
      iconName,
      id,
      innerRef,
      itemDisabledKey,
      itemRenderer,
      items,
      lazyLoadChildren,
      lazyLoadChildrenText,
      loading,
      loadingText,
      loadMore,
      nestedKey,
      onClick,
      onCloseMenu,
      onCreateMenuItems,
      onExpandCollapse,
      onLoadChildren,
      onSelectionChanged,
      pageStart,
      selection,
      selectionType,
      showHeader,
      style,
      ...rest
    } = this.props;
    const { expanded } = this.state;
    const numSelected = TreeSelectionHandler.getNumberOfSelectedItems(selection);
    return (
      <table
        {...rest}
        className={cx(
          'core-tree core-table core-infinite-scroll-wrapper',
          className,
          {
            'multi-select': numSelected > 1,
            'no-header': !showHeader,
          })
        }
        id={id}
        ref={ref => {
          this.refTree = ref;
          innerRef(ref);
        }}
        style={style}
      >
        {children}
        {showHeader && <thead className="tree-header" ><tr>
          {columns.map((column, index) => (
            <th key={index}>{columnNames ? columnNames[index] : column}</th>
          ))}
          {onCreateMenuItems && <th className="menu" />}
        </tr></thead>}
        <InfiniteScroll
          className={cx({ 'header-included': showHeader })}
          component="tbody"
          hasMore={hasMore}
          loadMore={loadMore}
          pageStart={pageStart}
        >
          {items && items.length ?
            <Node
              columns={columns}
              expanded={expanded}
              iconName={iconName}
              itemDisabledKey={itemDisabledKey}
              itemRenderer={itemRenderer}
              items={items}
              lazyLoadChildren={lazyLoadChildren}
              lazyLoadChildrenText={lazyLoadChildrenText}
              loadingText={loadingText}
              nestedKey={nestedKey}
              onClick={this.onClick}
              onCloseMenu={onCloseMenu}
              onCreateMenuItems={onCreateMenuItems}
              onExpandCollapse={this.onExpandCollapse}
              onLoadChildren={this.onLoadChildren}
              onSelectionChanged={onSelectionChanged}
              selection={selection}
              selectionHandler={this.selectionHandler}
              width={style.width}
            /> :
            <LoadingIndicator message={!items ? emptyText : loadingText} />}
        </InfiniteScroll>
        {loading && <tfoot><tr><td>
          <Progressbar
            className="core-infinite-scroll-progress"
            circular
            indeterminate
          />
        </td></tr></tfoot>}
      </table>
    );
  }

}
